/**
 * Pagina /tesoreria/casas — vista global por unidad (Sprint 17 Fase 2).
 *
 * Dos sub-vistas via toggle: grilla anual (default) y tabla. Filtros
 * chips por estado con counts. Buscador por nº casa o titular. Click
 * en una casa abre panel lateral con saldo + acciones.
 *
 * Arquitectura View + wrapper.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getCasasAnual,
  type CasaAnualResumen,
  type CasasAnualResponse,
  type EstadoCasa,
} from '../../lib/api/casas-tesoreria';
import { Card } from '../../components/ui/Card';
import { GrillaCasasAnual } from '../../components/tesoreria/GrillaCasasAnual';
import { TablaCasas } from '../../components/tesoreria/TablaCasas';
import { PanelCasa } from '../../components/tesoreria/PanelCasa';

type Vista = 'grilla' | 'tabla';
// 'con_saldo_contra' (C4) NO es un estado: filtra por saldo_total_pendiente > 0
// (cualquier saldo en contra). Es el destino del link de la tarjeta del
// dashboard "Casas con saldo en contra" — mismo criterio (deuda > 0).
// 'compensacion' NO es un estado de casa (rediseño 2026-06-25): filtra por
// el flag `tiene_compensacion`. Listado de casas con alguna compensación.
type FiltroEstado = 'todas' | EstadoCasa | 'con_saldo_contra' | 'compensacion';

// Sprint 17 S17-Z5: chip nuevo "Al día (con saldo)" para distinguir
// casas que pagaron hasta el mes corriente pero deben resto del semestre.
const FILTROS: { id: FiltroEstado; label: string }[] = [
  { id: 'todas', label: 'Todas' },
  { id: 'con_saldo_contra', label: 'Con saldo en contra' },
  { id: 'al_dia', label: 'Al día' },
  { id: 'al_dia_pendiente_semestre', label: 'Al día (con saldo)' },
  { id: 'atrasada', label: 'Atrasadas' },
  { id: 'suspendida', label: 'Suspendidas' },
  { id: 'compensacion', label: 'Compensación' },
];

const FILTROS_VALIDOS = FILTROS.map((f) => f.id);

export interface CasasViewProps {
  data: CasasAnualResponse | null;
  loading: boolean;
  error: string | null;
  // Pieza B: recarga la grilla tras aplicar saldo a favor desde el panel.
  onRecargar?: () => void;
}

export function CasasView({
  data,
  loading,
  error,
  onRecargar,
}: CasasViewProps) {
  const [searchParams] = useSearchParams();
  const [vista, setVista] = useState<Vista>('grilla');
  // Filtro inicial desde `?filtro=` (ej. el link de la tarjeta del dashboard
  // a `?filtro=con_saldo_contra`); cae a 'todas' si no es válido.
  const [filtro, setFiltro] = useState<FiltroEstado>(() => {
    const f = searchParams.get('filtro');
    return f && (FILTROS_VALIDOS as string[]).includes(f)
      ? (f as FiltroEstado)
      : 'todas';
  });
  // Sprint 17 Z16 — filtro por sector (Oriente / Poniente / Todos).
  const [filtroSector, setFiltroSector] = useState<string>('todos');
  const [busqueda, setBusqueda] = useState('');
  const [seleccionada, setSeleccionada] = useState<CasaAnualResumen | null>(
    null,
  );

  // Sectores únicos detectados en data.
  const sectoresDisponibles = useMemo(() => {
    if (!data) return [] as string[];
    const set = new Set<string>();
    for (const c of data.casas) {
      if (c.sector) set.add(c.sector);
    }
    return Array.from(set).sort();
  }, [data]);

  const casasFiltradas = useMemo(() => {
    if (!data) return [];
    const q = busqueda.trim().toLowerCase();
    return data.casas.filter((c) => {
      if (filtro === 'con_saldo_contra') {
        // Saldo neto a hoy > 0 (descontado el saldo a favor): una casa
        // cuyo crédito cubre su deuda NO está "en contra".
        if (c.saldo_a_hoy <= 0) return false;
      } else if (filtro === 'compensacion') {
        // No es un estado: filtra las casas con ≥1 compensación activa.
        if (!c.tiene_compensacion) return false;
      } else if (filtro !== 'todas' && c.estado !== filtro) {
        return false;
      }
      if (filtroSector !== 'todos' && c.sector !== filtroSector) return false;
      if (q) {
        const matchCasa = c.identificador.toLowerCase().includes(q);
        const matchTitular = (c.titular_nombre || '').toLowerCase().includes(q);
        if (!matchCasa && !matchTitular) return false;
      }
      return true;
    });
  }, [data, filtro, filtroSector, busqueda]);

  const anio = data?.anio ?? new Date().getFullYear();

  return (
    <div className="flex">
      <div className="flex-1 px-4 md:px-8 py-6 space-y-6 min-w-0">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-widest text-orange font-medium">
              Tesorería
            </div>
            <h1 className="text-2xl font-medium text-text-primary mt-1">
              Estado por casa
            </h1>
            <p className="text-sm text-text-secondary mt-1 max-w-2xl">
              Vista global del año {anio}. Click en una casa para ver detalle.
            </p>
          </div>
          {/* Toggle vista */}
          <div className="inline-flex border border-white/[0.08] rounded overflow-hidden text-xs">
            {(['grilla', 'tabla'] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setVista(v)}
                className={`px-3 py-1.5 ${
                  vista === v
                    ? 'bg-bg-card text-text-primary'
                    : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                {v === 'grilla' ? 'Grilla' : 'Tabla'}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded p-4 text-sm text-red-300">
            {error}
          </div>
        )}

        {/* Chips de filtro */}
        <div className="flex items-center gap-2 flex-wrap">
          {FILTROS.map((f) => {
            const count =
              f.id === 'todas'
                ? (data?.casas.length ?? 0)
                : f.id === 'con_saldo_contra'
                  ? (data?.casas.filter((c) => c.saldo_a_hoy > 0).length ?? 0)
                  : (data?.conteos[f.id] ?? 0);
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => setFiltro(f.id)}
                className={`text-xs px-3 py-1.5 rounded-full border ${
                  filtro === f.id
                    ? 'border-orange text-orange bg-orange/10'
                    : 'border-white/[0.08] text-text-secondary hover:text-text-primary hover:border-white/[0.16]'
                }`}
              >
                {f.label}
                <span className="ml-1.5 text-text-muted">{count}</span>
              </button>
            );
          })}
          <input
            type="text"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar casa o titular…"
            className="text-xs bg-bg-sunken border border-white/[0.08] rounded px-3 py-1.5 text-text-primary focus:outline-none focus:border-orange ml-auto w-full sm:w-56"
          />
        </div>

        {/* Sprint 17 Z16 — chips de filtro por sector */}
        {sectoresDisponibles.length > 1 && (
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <span className="text-text-muted">Sector:</span>
            {(['todos', ...sectoresDisponibles] as const).map((s) => {
              const count =
                s === 'todos'
                  ? (data?.casas.length ?? 0)
                  : (data?.casas.filter((c) => c.sector === s).length ?? 0);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => setFiltroSector(s)}
                  className={`px-2.5 py-1 rounded-full border ${
                    filtroSector === s
                      ? 'border-orange text-orange bg-orange/10'
                      : 'border-white/[0.08] text-text-secondary hover:text-text-primary hover:border-white/[0.16]'
                  }`}
                >
                  {s === 'todos' ? 'Todos' : s}
                  <span className="ml-1 text-text-muted">{count}</span>
                </button>
              );
            })}
          </div>
        )}

        <Card>
          <div className="p-4">
            {loading ? (
              <SkeletonGrilla />
            ) : vista === 'grilla' ? (
              <GrillaCasasAnual
                casas={casasFiltradas}
                onCasaClick={setSeleccionada}
              />
            ) : (
              <TablaCasas
                casas={casasFiltradas}
                onCasaClick={setSeleccionada}
              />
            )}
          </div>
        </Card>
      </div>

      <PanelCasa
        casa={seleccionada}
        onClose={() => setSeleccionada(null)}
        onAplicado={onRecargar}
      />
    </div>
  );
}

// Sprint 17 S17-Z1: skeleton de carga mientras la grilla se está
// resolviendo. Reemplaza el "Cargando casas…" textual.
function SkeletonGrilla() {
  return (
    <div className="space-y-2" aria-busy="true" aria-label="Cargando casas">
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          className="grid grid-cols-[200px_1fr_130px_140px] gap-3 items-center py-1.5 animate-pulse"
        >
          <div>
            <div className="h-3.5 w-20 bg-white/[0.07] rounded" />
            <div className="h-2.5 w-32 bg-white/[0.04] rounded mt-1.5" />
          </div>
          <div className="h-3 w-full bg-white/[0.04] rounded" />
          <div className="h-4 w-20 bg-white/[0.05] rounded mx-auto" />
          <div className="h-3 w-16 bg-white/[0.04] rounded ml-auto" />
        </div>
      ))}
    </div>
  );
}

export function CasasPage() {
  const [data, setData] = useState<CasasAnualResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const recargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await getCasasAnual());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar casas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void recargar();
  }, [recargar]);

  return (
    <CasasView
      data={data}
      loading={loading}
      error={error}
      onRecargar={recargar}
    />
  );
}
